import { ClienteFincaLoteItem } from "../../../generated/grpc/terceros_lote_pb";
import { grpcLoteDatabase } from "../database/GrpcLoteDatabase";
import { ClienteFincaLoteResponse } from "../response/ClienteFincaLoteResponse";

/** Nombres actuales de los lotes de una finca, por id, leídos de la copia
 *  local de lotes (`grpcLoteDatabase`), sin pedir nada al server.
 *
 *  Las listas de compras, ventas y eventos guardan el nombre del lote tal
 *  como estaba cuando se sincronizó cada registro. Al renombrar un lote, el
 *  server no marca esos registros como cambiados y el delta nunca los vuelve
 *  a traer, así que quedaban con el nombre viejo. Sus servicios de listado
 *  usan este mapa para mostrar siempre el nombre actual (2026-10-08). */
export async function fx_nombresLotesAsync(
  fincaId: string,
): Promise<ReadonlyMap<string, string>> {
  const lotes = await grpcLoteDatabase.getListByFincaIdAsync<ClienteFincaLoteItem>(fincaId);
  return new Map(lotes.map((lote) => [lote.id, lote.nombreLote]));
}

/** Nombre a mostrar de un lote: el actual de la copia local si se conoce
 *  (ver `fx_nombresLotesAsync`); si no, el que vino con el registro. */
export function fx_nombreLote(
  nombresLotes: ReadonlyMap<string, string> | undefined,
  loteId: string,
  nombreDelRegistro: string,
): string {
  return nombresLotes?.get(loteId) ?? nombreDelRegistro;
}

/** Construye un `ClienteFincaLoteResponse` de relleno a partir de solo
 *  `id`/`nombreLote` — usado donde REST embebía el lote completo
 *  (`CompraAnimalesResponse.loteEntrada`/`loteActual`,
 *  `VentaAnimalesResponse.loteSalida`) pero se confirmó que el cliente nunca
 *  lee de ahí más que esos dos campos (ver `pesaje_ganaderia.proto`): los
 *  gRPC de Compra/Venta ahora solo mandan id+nombre por lote, no el objeto
 *  entero. El resto de los campos quedan con valores inertes -nada los lee
 *  en este contexto-, nunca `null`/`undefined` porque el tipo los pide
 *  presentes. */
export function toLoteResumen(
  id: string,
  nombreLote: string,
): ClienteFincaLoteResponse {
  return {
    id,
    isEliminado: false,
    clienteId: "",
    fincaId: "",
    nombreLote,
    areaHectareas: 0,
    areaCuadras: 0,
    isAreaProductiva: false,
    areaGeograficaPolygon: { type: "Polygon", coordinates: [] },
    loteOrigenId: null,
    cultivo: null,
    ganaderiaCantidadAnimales: 0,
    ganaderiaUltimoIngreso: null,
    ganaderiaUltimaSalida: null,
    ganaderiaPesoRealPromedioKg: null,
    ganaderiaFechaUltimoPesaje: null,
    ganaderiaPesoEstimadoTotalKg: null,
    ganaderiaFechaUltimoPesoEstimado: null,
    ganaderiaGananciaDiariaTotalKg: null,
    monitoreoCantidadArboles: 0,
  };
}
